import { useMemo } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { TimedSessionResult } from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import { Card } from "../primitives/Card";
import { MiniTrendBars } from "../primitives/MiniTrendBars";
import { computeWeeklyCounts, FREQ_WEEKS } from "../Progress/progressMath";

// The coach's rhythm panel: weekly session counts bucketed from the SAME
// session-results snapshot every other panel reads (passed in — this card never
// fetches). Split out of AthleteDetail (CO-7) to make room for the targets
// panel without widening that file past the cap; the math and the copy are
// unchanged.
//
// `results === null` means still loading, NOT "no sessions" — hence the spinner
// rather than a zero. An athlete with a real zero week renders bars of zero.

export function CoachRhythmCard({
  theme,
  results,
}: {
  theme: UiTheme;
  results: TimedSessionResult[] | null;
}) {
  const { t, i18n } = useTranslation();
  const styles = makeStyles(theme);

  const rhythm = useMemo(() => {
    if (!results) return null;
    const weekly = computeWeeklyCounts(
      results.map((r) => ({ startedAt: new Date(r.startedAtMs).toISOString() })),
      Date.now(),
    );
    const lastWeek = weekly[weekly.length - 1] ?? 0;
    const avg =
      Math.round((weekly.reduce((n, c) => n + c, 0) / weekly.length) * 10) / 10;
    return { weekly, lastWeek, avg };
  }, [results]);

  return (
    <Card theme={theme}>
      <Text style={styles.cardTitle} accessibilityRole="header">
        {t("mobile.progress.frequencyTitle")}
      </Text>
      {rhythm ? (
        <>
          <Text style={styles.cardBody}>
            {t("coach.detail.rhythmSummary", {
              avg: rhythm.avg.toLocaleString(i18n.language),
              last: rhythm.lastWeek,
              weeks: FREQ_WEEKS,
            })}
          </Text>
          <View style={styles.rhythmBars}>
            <MiniTrendBars
              theme={theme}
              weeks={rhythm.weekly}
              height={40}
              barWidth={12}
              gap={4}
            />
          </View>
        </>
      ) : (
        <ActivityIndicator
          color={theme.palette.ink2}
          accessibilityLabel={t("app.loading")}
        />
      )}
    </Card>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    cardTitle: {
      color: p.ink,
      fontSize: ty.sizes.md,
      fontWeight: "700",
      marginBottom: s.s2,
    },
    cardBody: { color: p.ink2, fontSize: ty.sizes.sm },
    rhythmBars: { marginTop: s.s3, alignItems: "flex-start" },
  });
}
